"use client";

import { useCallback, useMemo, useRef, useState } from "react";
import { StateStore } from "../context/state-store";
import { isPlainObject } from "./StudioScreenRenderer.utils.data";
import { usePrePaintEffect } from "./StudioScreenRenderer.pre-paint";

/** Keep one observable store and a current form snapshot per renderer instance. */
export function useScreenState() {
  const store = useMemo(() => new StateStore(), []);
  const [, forceRender] = useState(0);

  // Subscribe PRE-PAINT. The bootstrap effect (and the mount flow's
  // optimistic localStorage paint) run before the first frame composes;
  // subscribing with a passive effect would miss those notifications on
  // mount, so the cached snapshot was never committed and users saw the
  // placeholder until the network response arrived.
  usePrePaintEffect(
    () =>
      store.subscribe(() => {
        forceRender((value) => value + 1);

        // Flows mutate form fields through `form:set` on the execution
        // context, which writes the store's "form" slot — not this React form
        // state. Sync those flow-authored values (e.g. clearing the contact
        // fields after a successful submit) back into the React form state so
        // bound controls actually reflect the cleared values.
        const flowForm = store.get("form");
        if (!isPlainObject(flowForm)) return;

        setFormValues((previous) => {
          let changed = false;
          const next = { ...previous };
          for (const [field, value] of Object.entries(flowForm)) {
            if (!Object.is(next[field], value)) {
              next[field] = value;
              changed = true;
            }
          }
          return changed ? next : previous;
        });
      }),
    [store],
  );

  const [formValues, setFormValues] = useState<Record<string, unknown>>({});
  const formRef = useRef(formValues);
  formRef.current = formValues;
  const setFormValue = useCallback(
    (field: string, value: unknown) =>
      setFormValues((previous) => ({ ...previous, [field]: value })),
    [],
  );
  const getFormValues = useCallback(() => formRef.current, []);
  return { store, formRef, setFormValue, getFormValues };
}